import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DateTime';
import { dateValidation } from '../../shared-functions/Validation';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string;
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  disablePastDates?: boolean;
  minDate?: string;
  maxDate?: string;
}

/**
 * Custom Date Pickers component
 * @param {CustomDateRangeProperties} properties - Component properties
 * @returns The rendered CustomDatePickers component
 */
function CustomDatePickers(properties: CustomDateRangeProperties) {
  const {
    inputLabel,
    name,
    value,
    placeholder,
    onChangeValue,
    valueRequired,
    readOnly,
    disablePastDates,
    minDate,
    maxDate,
  } = properties;

  const reference = useRef<HTMLDivElement>(null);
  const inputContainerReference = useRef<HTMLDivElement>(null);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [allDates, setAllDates] = useState<any[]>([]);
  const [style, setStyle] = useState<any>({});
  const [error, setError] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);

  /**
   * Recalculates and updates the calendar portal position relative to the input container
   */
  const updatePosition = useCallback(() => {
    if (inputContainerReference.current) {
      const rect = inputContainerReference.current.getBoundingClientRect();
      const pickerWidth = 310;
      const pickerHeight = 280;
      const margin = 8;
      const { innerHeight: viewportHeight, innerWidth: viewportWidth } = window;

      if (rect.bottom < 0 || rect.top > viewportHeight) {
        setOpen(false);
      } else {
        let top = rect.bottom + 4;
        if (
          rect.bottom + pickerHeight + margin > viewportHeight &&
          rect.top > pickerHeight + margin
        ) {
          top = rect.top - pickerHeight - 4;
        }

        let {left} = rect;
        if (rect.left + pickerWidth + margin > viewportWidth) {
          left = viewportWidth - pickerWidth - margin;
        }

        setStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${Math.max(margin, left)}px`,
          zIndex: 13000,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    updatePosition();

    /**
     * Handles window scroll and resize events to keep calendar attached to input container
     */
    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, updatePosition]);

  /**
   * Handle the Value change
   * @param {string} changeValue - The changeValue
   */
  const handleValueChange = (changeValue: string) => {
    onChangeValue(name, changeValue);
    setOpen(false);
    setError(!dateValidation(changeValue));
  };

  /**
   * Handle the custom Value change
   * @param {string} changeValue - The changeValue
   */
  const handleCustomValueChange = (changeValue: string) => {
    handleValueChange(changeValue);
  };

  /**
   * Handle the Input click
   */
  const openCalenderView = () => {
    if (!readOnly) {
      setOpen(true);
      updatePosition();
    }
  };

  /**
   * Get all days for a given month and year
   * @param {number} month - The month index (0-11)
   * @param {number} year - The year
   */
  const getAllDays = useCallback(
    (month: number, year: number) => {
      const date = new Date(year, month, 1);
      let days = [];
      while (date.getMonth() === month) {
        days.push(new Date(date));
        date.setDate(date.getDate() + 1);
      }
      days = days.map((valueChange) => {
        const formattedDate = changeDateFormat(valueChange.toString());
        let isDisabled = false;
        if (disablePastDates) {
          const today = changeDateFormat(new Date().toString());
          isDisabled = formattedDate < today;
        }
        if (minDate && !isDisabled) {
          const formattedMinDate = changeDateFormat(minDate);
          isDisabled = formattedDate < formattedMinDate;
        }
        if (maxDate && !isDisabled) {
          const formattedMaxDate = changeDateFormat(maxDate);
          isDisabled = formattedDate > formattedMaxDate;
        }
        return {
          date: formattedDate,
          number: valueChange.getDate(),
          day: valueChange.getDay(),
          isDisabled,
        };
      });
      if (days[0].day > 0) {
        const firstMonth = days[0].day;
        for (let index = 0; index < firstMonth; index += 1) {
          days = [{}, ...days];
        }
      }
      setAllDates(days);
    },
    [disablePastDates, minDate, maxDate],
  );

  /**
   * Handle the month change function
   * @param {string} type - The type of change ('add' or 'sub')
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Handle close the calendar overflow click function
   * @param {MouseEvent} event - The mouse event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (
      reference.current &&
      !reference.current.contains(event.target as Node) &&
      inputContainerReference.current &&
      !inputContainerReference.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    getAllDays(currentMonth, currentYear);
  }, [currentMonth, currentYear, getAllDays]);

  return (
    <div className="custom-input-date-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-date-input">
        <div
          ref={inputContainerReference}
          className={`custom-input-container ${error ? 'error' : ''}`}
          onClick={openCalenderView}
          role="presentation">
          <input
            type="text"
            name="date"
            value={value}
            placeholder={placeholder}
            onChange={(event: any) => handleCustomValueChange(event.target.value)}
          />
          <span>
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div className="calender-box" ref={reference} style={style}>
              <div className="calender-header">
                <div
                  className="calender-header-left"
                  onClick={() => changeMonth('sub')}
                  role="presentation">
                  <LeftArrow />
                </div>
                <div className="calender-header-center">
                  {months[currentMonth]} - {currentYear}
                </div>
                <div
                  className="calender-header-right"
                  onClick={() => changeMonth('add')}
                  role="presentation">
                  <RightArrow />
                </div>
              </div>
              <div className="calender-body">
                <div className="calender-header-items">
                  <div className="header-item">SUN</div>
                  <div className="header-item">MON</div>
                  <div className="header-item">TUS</div>
                  <div className="header-item">WEN</div>
                  <div className="header-item">THU</div>
                  <div className="header-item">FRI</div>
                  <div className="header-item">SAT</div>
                </div>
                <div className="calender-body-items">
                  {allDates.map((dateDetail: any, index: number) =>
                    Object.keys(dateDetail).length === 0 ? (
                      <div key={`empty-${index}`} className="header-item" />
                    ) : (
                      <div
                        key={dateDetail.date}
                        className={`header-item ${dateDetail.isDisabled ? 'disabled' : ''}`}
                        onClick={() => !dateDetail.isDisabled && handleValueChange(dateDetail.date)}
                        role="presentation">
                        {dateDetail.number}
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

CustomDatePickers.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  readOnly: false,
  disablePastDates: false,
  minDate: '',
  maxDate: '',
};

export default CustomDatePickers;
